/**
 * DEFCON TERMINAL - Main Terminal Class
 * Handles terminal display, screen buffer, input, and output
 */

import { ScreenBuffer } from './ScreenBuffer';
import { TextRenderer, type RenderOptions } from './TextRenderer';
import { InputHandler, type InputEvent } from './InputHandler';
import { TIMING, ASCII_DIMENSIONS, TERMINAL_COLORS } from '@shared/constants';

/** Terminal event types */
export type TerminalEventType = 'command' | 'input' | 'keypress' | 'ready' | 'clear';

/** Terminal event payload */
export interface TerminalEvent {
  type: TerminalEventType;
  data: string;
  timestamp: number;
}

/** Terminal event listener callback */
export type TerminalEventListener = (event: TerminalEvent) => void;

/** Terminal configuration options */
export interface TerminalConfig {
  width?: number;
  height?: number;
  typingSpeed?: number;
  cursorBlink?: boolean;
  soundEnabled?: boolean;
  crtEffects?: boolean;
}

/** Color codes for ANSI-like support */
export const COLOR_CODES: Record<string, string> = {
  '\\c[green]': 'color-green',
  '\\c[dim]': 'color-dim',
  '\\c[bright]': 'color-bright',
  '\\c[red]': 'color-red',
  '\\c[yellow]': 'color-yellow',
  '\\c[amber]': 'color-amber',
  '\\c[cyan]': 'color-cyan',
  '\\c[white]': 'color-white',
  '\\c[reset]': '',
};

/**
 * Main Terminal class
 * Manages the terminal display, input handling, and text rendering
 */
export class Terminal {
  private outputElement: HTMLElement;
  private inputElement: HTMLInputElement;
  private cursorElement: HTMLElement;
  private screenElement: HTMLElement;

  private screenBuffer: ScreenBuffer;
  private textRenderer: TextRenderer;
  private inputHandler: InputHandler;

  private eventListeners: Map<TerminalEventType, Set<TerminalEventListener>>;
  private config: Required<TerminalConfig>;

  private pendingPrompt: {
    resolve: (value: string) => void;
    echo: boolean;
    echoPrefix: string;
  } | null = null;

  private isReady: boolean = false;
  private isAnimating: boolean = false;
  private animationQueue: Array<() => Promise<void>> = [];
  private clickHandler: (() => void) | null = null;

  constructor(config: TerminalConfig = {}) {
    // Set default configuration
    this.config = {
      width: config.width ?? ASCII_DIMENSIONS.terminalWidth,
      height: config.height ?? ASCII_DIMENSIONS.terminalHeight,
      typingSpeed: config.typingSpeed ?? TIMING.typingSpeed,
      cursorBlink: config.cursorBlink ?? true,
      soundEnabled: config.soundEnabled ?? false,
      crtEffects: config.crtEffects ?? true,
    };

    // Initialize event listeners map
    this.eventListeners = new Map();

    // Get DOM elements
    this.outputElement = document.getElementById('terminal-output') as HTMLElement;
    this.inputElement = document.getElementById('terminal-input') as HTMLInputElement;
    this.cursorElement = document.querySelector('.cursor') as HTMLElement;
    this.screenElement = document.querySelector('.crt-screen') as HTMLElement;

    if (!this.outputElement || !this.inputElement) {
      throw new Error('Terminal: Required DOM elements not found');
    }

    // Initialize components
    this.screenBuffer = new ScreenBuffer(this.config.width, this.config.height);
    this.textRenderer = new TextRenderer(this.outputElement, {
      defaultSpeed: this.config.typingSpeed,
    });
    this.inputHandler = new InputHandler(this.inputElement, {
      historySize: 50,
      tabCompletionEnabled: true,
    });

    // Setup event handlers
    this.setupEventHandlers();

    // Apply CRT effects if enabled
    if (this.config.crtEffects) {
      this.enableCRTEffects();
    }

    // Mark as ready
    this.isReady = true;
    this.emit('ready', 'Terminal initialized');
  }

  /**
   * Setup internal event handlers
   */
  private setupEventHandlers(): void {
    // Handle input submission
    this.inputHandler.on('submit', (event: InputEvent) => {
      this.handleSubmit(event.value);
      // Reset cursor position after input is cleared
      this.updateCursorPosition();
    });

    // Handle keypress events
    this.inputHandler.on('keypress', (event: InputEvent) => {
      this.emit('keypress', event.value);
    });

    // Handle input changes to update cursor position
    this.inputHandler.on('change', () => {
      this.updateCursorPosition();
    });

    // Also update on history navigation, tab completion, and escape
    this.inputHandler.on('history', () => {
      this.updateCursorPosition();
    });
    this.inputHandler.on('tab', () => {
      this.updateCursorPosition();
    });
    this.inputHandler.on('escape', () => {
      this.updateCursorPosition();
    });

    // Focus input on terminal click (store reference for cleanup)
    this.clickHandler = () => this.inputElement.focus();
    this.outputElement.addEventListener('click', this.clickHandler);

    // Initial cursor position
    this.updateCursorPosition();
  }

  /**
   * Update cursor position based on input text width
   */
  private updateCursorPosition(): void {
    if (!this.cursorElement) return;

    // Create a temporary span to measure text width
    const measureSpan = document.createElement('span');
    measureSpan.style.visibility = 'hidden';
    measureSpan.style.position = 'absolute';
    measureSpan.style.whiteSpace = 'pre';
    measureSpan.style.font = window.getComputedStyle(this.inputElement).font;
    measureSpan.textContent = this.inputElement.value || '';

    document.body.appendChild(measureSpan);
    const textWidth = measureSpan.offsetWidth;
    document.body.removeChild(measureSpan);

    // Position cursor after the text
    this.cursorElement.style.left = `${textWidth}px`;
  }

  /**
   * Handle input submission
   */
  private handleSubmit(value: string): void {
    // Check for hidden Falcon easter egg command - opens T.I.T.A.N. in new tab
    if (value.toLowerCase() === 'login_falcon') {
      this.openFalconSystem();
      return;
    }

    if (this.pendingPrompt) {
      const { resolve, echo, echoPrefix } = this.pendingPrompt;

      // Echo the input if needed (print newline after inline prompt + input)
      if (echo) {
        this.println(`${echoPrefix}${value}`);
      } else {
        // Just print a newline to move to next line after input
        this.println(value);
      }

      resolve(value);
      return;
    }

    this.handleCommand(value);
  }

  /**
   * Open the T.I.T.A.N. system in a new browser tab
   */
  private openFalconSystem(): void {
    // Don't echo the command - it's hidden
    this.println('');
    this.println('\\c[dim]FALKEN SYSTEM DETECTED\\c[reset]');
    this.println('\\c[dim]Establishing secure connection...\\c[reset]');
    this.println('');

    // Open T.I.T.A.N. in a new tab after brief delay
    setTimeout(() => {
      const falconUrl = this.getFalconSystemUrl();
      window.open(falconUrl, '_blank');
    }, 800);
  }

  /**
   * Get the T.I.T.A.N. system URL
   */
  private getFalconSystemUrl(): string {
    // Use /falcon route — Cloudflare Pages _redirects maps this to falcon.html
    return '/falcon';
  }

  /**
   * Get the admin panel URL
   */
  private getAdminPanelUrl(): string {
    return 'https://defcon-terminal-api-production.avenislabs.workers.dev/admin';
  }

  /**
   * Handle command submission
   */
  private handleCommand(command: string): void {
    // Echo command to terminal
    this.println(`> ${command}`);

    // Emit command event
    this.emit('command', command);

    // Clear input
    this.inputElement.value = '';
  }

  /**
   * Enable CRT visual effects
   */
  private enableCRTEffects(): void {
    if (this.screenElement) {
      this.screenElement.classList.add('power-on');
    }
  }

  /**
   * Disable CRT visual effects
   */
  public disableCRTEffects(): void {
    if (this.screenElement) {
      this.screenElement.classList.remove('power-on');
    }
  }

  /**
   * Clear the terminal screen
   */
  public clear(): void {
    this.outputElement.innerHTML = '';
    this.screenBuffer.clear();
    this.emit('clear', '');
  }

  /**
   * Print text without newline
   */
  public print(text: string): void {
    const line = this.createLineElement(text);
    this.outputElement.appendChild(line);
    this.scrollToBottom();
  }

  /**
   * Print text with newline
   */
  public println(text: string = ''): void {
    const line = this.createLineElement(text);
    this.outputElement.appendChild(line);
    this.scrollToBottom();
  }

  /**
   * Print multiple lines
   */
  public printLines(lines: string[]): void {
    lines.forEach(line => this.println(line));
  }

  /**
   * Print text with typewriter effect
   */
  public async printWithDelay(
    text: string,
    speed: number = this.config.typingSpeed
  ): Promise<void> {
    const line = document.createElement('div');
    line.className = 'terminal-line';
    this.outputElement.appendChild(line);

    await this.textRenderer.typeText(line, text, {
      speed,
      parseColors: true,
    });

    this.scrollToBottom();
  }

  /**
   * Print multiple lines with delay between each
   */
  public async printLinesWithDelay(
    lines: string[],
    charSpeed: number = this.config.typingSpeed,
    lineDelay: number = TIMING.lineDelay
  ): Promise<void> {
    for (const text of lines) {
      await this.printWithDelay(text, charSpeed);
      await this.delay(lineDelay);
    }
  }

  /**
   * Create a styled line element
   */
  private createLineElement(text: string): HTMLElement {
    const line = document.createElement('div');
    line.className = 'terminal-line';

    // Parse and apply color codes
    const parsedContent = this.parseColorCodes(text);
    line.innerHTML = parsedContent;

    return line;
  }

  /**
   * Parse ANSI-like color codes in text
   */
  private parseColorCodes(text: string): string {
    let result = text;
    let currentClass = '';

    // Replace color codes with spans
    for (const [code, className] of Object.entries(COLOR_CODES)) {
      const escapedCode = code.replace(/[[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedCode, 'g');

      if (className === '') {
        // Reset code - close any open span
        result = result.replace(regex, currentClass ? '</span>' : '');
        currentClass = '';
      } else {
        // Color code - open a new span
        result = result.replace(regex, (match) => {
          const closeTag = currentClass ? '</span>' : '';
          currentClass = className;
          return `${closeTag}<span class="${className}">`;
        });
      }
    }

    // Close any unclosed span
    if (currentClass) {
      result += '</span>';
    }

    // Escape HTML but preserve our spans
    result = this.escapeHtmlPreservingSpans(result);

    return result;
  }

  /**
   * Escape HTML while preserving span tags
   */
  private escapeHtmlPreservingSpans(text: string): string {
    // Extract spans
    const spanPattern = /<\/?span[^>]*>/g;
    const spans: string[] = [];
    let index = 0;

    const placeholder = (i: number) => `__SPAN_${i}__`;

    // Replace spans with placeholders
    const withPlaceholders = text.replace(spanPattern, (match) => {
      spans.push(match);
      return placeholder(index++);
    });

    // Actually escape HTML entities to prevent XSS
    const escaped = withPlaceholders
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    // Restore spans after escaping
    let result = escaped;
    spans.forEach((span, i) => {
      result = result.replace(placeholder(i), span);
    });

    return result;
  }

  /**
   * Scroll terminal output to bottom
   */
  private scrollToBottom(): void {
    this.outputElement.scrollTop = this.outputElement.scrollHeight;
  }

  /**
   * Utility delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Add event listener
   */
  public on(event: TerminalEventType, listener: TerminalEventListener): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(listener);
  }

  /**
   * Remove event listener
   */
  public off(event: TerminalEventType, listener: TerminalEventListener): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(listener);
    }
  }

  /**
   * Emit event to all listeners
   */
  private emit(type: TerminalEventType, data: string): void {
    const event: TerminalEvent = {
      type,
      data,
      timestamp: Date.now(),
    };

    const listeners = this.eventListeners.get(type);
    if (listeners) {
      listeners.forEach(listener => listener(event));
    }
  }

  /**
   * Set input prompt text
   */
  public setPrompt(prompt: string): void {
    const promptElement = document.querySelector('.terminal-prompt');
    if (promptElement) {
      promptElement.textContent = prompt;
    }
  }

  /**
   * Prompt for input and resolve with the entered value
   */
  public prompt(
    text: string,
    options?: { echo?: boolean; echoPrefix?: string }
  ): Promise<string> {
    // Cancel any existing prompt
    if (this.pendingPrompt) {
      this.pendingPrompt.resolve('');
      this.pendingPrompt = null;
    }

    // Print the prompt text inline (no newline at end)
    this.print(text);
    this.setInputEnabled(true);
    this.setInputValue('');
    this.focus();

    return new Promise((resolve) => {
      this.pendingPrompt = {
        resolve: (value: string) => {
          this.pendingPrompt = null;
          resolve(value);
        },
        echo: options?.echo ?? false,
        echoPrefix: options?.echoPrefix ?? '',
      };
    });
  }

  /**
   * Wait for any keypress before continuing
   */
  public waitForKey(): Promise<string> {
    return new Promise((resolve) => {
      // Handler for regular keypresses
      const keypressHandler: TerminalEventListener = (event) => {
        cleanup();
        resolve(event.data);
      };

      // Handler for Enter key (which triggers 'command' event)
      const commandHandler: TerminalEventListener = (event) => {
        cleanup();
        resolve('Enter');
      };

      // Direct keydown listener for immediate response
      const keydownHandler = (e: KeyboardEvent) => {
        // Respond to any key
        cleanup();
        resolve(e.key);
      };

      const cleanup = () => {
        this.off('keypress', keypressHandler);
        this.off('command', commandHandler);
        this.inputElement.removeEventListener('keydown', keydownHandler);
        this.setInputValue('');
      };

      this.on('keypress', keypressHandler);
      this.on('command', commandHandler);
      // Also add direct keydown listener for immediate response
      this.inputElement.addEventListener('keydown', keydownHandler, { once: true });
      this.setInputEnabled(true);
      this.focus();
    });
  }

  /**
   * Enable/disable input
   */
  public setInputEnabled(enabled: boolean): void {
    this.inputElement.disabled = !enabled;
    if (enabled) {
      this.inputElement.focus();
    }
  }

  /**
   * Get input handler for tab completion setup
   */
  public getInputHandler(): InputHandler {
    return this.inputHandler;
  }

  /**
   * Get screen buffer for direct manipulation
   */
  public getScreenBuffer(): ScreenBuffer {
    return this.screenBuffer;
  }

  /**
   * Get text renderer for advanced rendering
   */
  public getTextRenderer(): TextRenderer {
    return this.textRenderer;
  }

  /**
   * Add flash effect to screen
   */
  public flashScreen(color: 'red' | 'green' | 'amber' = 'green', duration: number = 200): void {
    if (this.screenElement) {
      const flashClass = `animate-alert${color === 'red' ? '' : '-defcon3'}`;
      this.screenElement.classList.add(flashClass);
      setTimeout(() => {
        this.screenElement.classList.remove(flashClass);
      }, duration);
    }
  }

  /**
   * Show static noise effect
   */
  public showStatic(show: boolean): void {
    const staticElement = document.querySelector('.static-noise');
    if (staticElement) {
      staticElement.classList.toggle('active', show);
    }
  }

  /**
   * Play power on animation
   */
  public async powerOn(): Promise<void> {
    if (this.screenElement) {
      this.screenElement.classList.remove('power-off');
      this.screenElement.classList.add('power-on');
      await this.delay(2000);
    }
  }

  /**
   * Play power off animation
   */
  public async powerOff(): Promise<void> {
    if (this.screenElement) {
      this.screenElement.classList.remove('power-on');
      this.screenElement.classList.add('power-off');
      await this.delay(500);
    }
  }

  /**
   * Check if terminal is ready
   */
  public isTerminalReady(): boolean {
    return this.isReady;
  }

  /**
   * Focus the input field
   */
  public focus(): void {
    this.inputElement.focus();
  }

  /**
   * Get current input value
   */
  public getInputValue(): string {
    return this.inputElement.value;
  }

  /**
   * Set input value
   */
  public setInputValue(value: string): void {
    this.inputElement.value = value;
  }

  /**
   * Dispose of the terminal and clean up resources
   */
  public dispose(): void {
    // Remove click handler from output element to prevent memory leak
    if (this.clickHandler) {
      this.outputElement.removeEventListener('click', this.clickHandler);
      this.clickHandler = null;
    }

    // Clear all event listeners
    this.eventListeners.clear();

    // Dispose of input handler
    this.inputHandler.dispose();

    // Clear animation queue
    this.animationQueue = [];
    this.isAnimating = false;

    // Clear pending prompt if any
    this.pendingPrompt = null;

    // Clear screen buffer
    this.screenBuffer.clear();

    // Reset ready state
    this.isReady = false;
  }
}

export default Terminal;
